import AuthRepo from "@repositories/auth.repositories"
import {
  EditProfileResponse,
  GoogleLoginBody,
  GoogleLoginData,
  GoogleLoginResponse,
  GoogleNewUserResponse,
  LoginManualBody,
  LoginResponse,
  ProfileBody,
  RawTokenData,
  RawUserTraits,
  UserTraits,
} from "@interfaces/auth"
import { Result } from "@lib/api/services/result"
import { deleteTokenCookies, domainChecker, getTokenInfo, setTokenCookies } from "@lib/functions"
import { destroyCookie, setCookie } from "nookies"
import { ErrorResponse } from "@interfaces/response"

export default class AuthServices extends AuthRepo {
  async manualLogin({ email, password }: LoginManualBody) {
    try {
      const res = await this.rawLogin({ user_email: email, user_password: password })

      const response = await this._getResponse<RawTokenData | LoginResponse>(res)

      const { error, result, error_code } = response

      if (error || !result) {
        switch (error_code) {
          case "REQUIRED":
            return Result.fail<RawTokenData>("Terjadi Kesalahan. Silahkan coba lagi")
          case "WRONG_PASSWORD":
            return Result.fail<RawTokenData>("Password yang kamu masukkan salah")
          case "NOT_FOUND":
            return Result.fail<RawTokenData>("Email belum terdaftar")

          default: {
            return Result.fail<RawTokenData>()
          }
        }
      }

      const tokenData = setTokenCookies(result as LoginResponse)

      if (!tokenData) {
        return Result.fail<RawTokenData>(
          "Terjadi Kesalahan. Pastikan form terisi dengan benar dan silahkan coba lagi"
        )
      }

      if (tokenData.data?.status_completed_profile) {
        destroyCookie(null, "HS_TEMP")
      }

      return Result.ok<RawTokenData>(tokenData)
    } catch (e: any) {
      const errorData = await this._getErrorObject(e)
      const errorCode = errorData.error_code

      switch (errorCode) {
        case "NOT_VERIFIED":
          return Result.fail<RawTokenData>(
            "Akun ini belum diaktifkan. Silahkan cek email kamu untuk mengaktifkan akun"
          )
        case "DEACTIVATED":
          return Result.fail<RawTokenData>("Akun ini sudah dinonaktifkan")
        case "SUSPENDED":
          return Result.fail<RawTokenData>("Akun ini telah ditangguhkan")

        default: {
          return Result.fail<RawTokenData>("Terjadi kesalahan. Silahkan coba lagi")
        }
      }
    }
  }

  async googleLogin({ email, fullName, googleId }: GoogleLoginBody) {
    try {
      const res = await this.rawGoogleLogin({ userFullname: fullName, googleId, email })

      const response = await this._getResponse<GoogleLoginResponse | GoogleNewUserResponse>(res)

      const { result } = response

      if (!result) {
        return Result.fail<GoogleLoginData | GoogleNewUserResponse>(
          "Terjadi Kesalahan. Silahkan coba lagi"
        )
      }

      if (result.new_user === 0) {
        const loginResult = result as GoogleLoginResponse
        const rawTokenData = setTokenCookies(loginResult)

        if (!rawTokenData) {
          return Result.fail<GoogleLoginData>("Terjadi Kesalahan. Silahkan coba lagi")
        }

        destroyCookie(null, "HS_TEMP")
        const tokenData = rawTokenData.data
        return Result.ok<GoogleLoginData>({
          ...tokenData,
          new_user: 0,
        })
      }

      const loginResult = result as GoogleNewUserResponse
      setCookie(null, "HS_TEMP", JSON.stringify(loginResult), {
        path: "/",
        domain: domainChecker(),
      })

      return Result.ok<GoogleNewUserResponse>(loginResult)
    } catch (e: any) {
      const errorData = await this._getErrorObject(e)
      const errorCode = errorData.error_code

      switch (errorCode) {
        case "ACCOUNT_HAS_BEEN_DEACTIVATED":
          return Result.fail<GoogleNewUserResponse>("Akun ini sudah dinonaktifkan")
        case "USER_ACCOUNT_SUSPENDED":
          return Result.fail<GoogleNewUserResponse>("Akun ini telah ditangguhkan")
        case "USER_ALREADY_EXISTS":
          return Result.fail<GoogleNewUserResponse>(
            "Email sudah terdaftar, silahkan cek email dan verifikasi akun kamu."
          )

        default: {
          return Result.fail<GoogleNewUserResponse>("Terjadi Kesalahan. Silahkan coba lagi")
        }
      }
    }
  }

  async resendVerificationEmail(email: string) {
    try {
      const res = await this.rawResendVerification(email)

      const { error, result } = await this._getResponse<any>(res)

      if (error) {
        return Result.fail<any>()
      }

      return Result.ok<any>(result)
    } catch (e: any) {
      return Result.fail<any>()
    }
  }

  async refreshToken() {
    try {
      const { refreshToken } = getTokenInfo()

      const res = await this.rawRefreshToken({ refresh_token: refreshToken })

      const { error, result } = await this._getResponse<any>(res)

      if (error) {
        return Result.fail<any>()
      }

      setTokenCookies(result)
      return Result.ok<any>()
    } catch (e: any) {
      return Result.fail<any>()
    }
  }

  async logout() {
    deleteTokenCookies()

    try {
      await this.rawLogout()
    } catch (e: any) {
      // this.bugsnagNotifier(e)
    }
  }

  async getUserData() {
    try {
      const res = await this.rawGetUserData()

      if (!res.ok) {
        return Result.fail<UserTraits>("Terjadi Kesalahan. Silahkan coba lagi")
      }

      const result = await this._getRawResult<RawUserTraits>(res)

      return Result.ok<UserTraits>(result.traits)
    } catch (error) {
      return Result.fail<UserTraits>("Terjadi Kesalahan. Silahkan coba lagi")
    }
  }

  async editProfile(body: Partial<ProfileBody>) {
    try {
      const res = await this.rawUpdateProfile(body)

      const response = await this._getResponse<EditProfileResponse>(res)
      const { error, result, error_code } = response

      if (error || error_code || !result) {
        return Result.fail<EditProfileResponse>()
      }

      destroyCookie(null, "HS_TEMP")
      await this.refreshToken()

      return Result.ok<EditProfileResponse>(result)
    } catch (e: any) {
      return Result.fail<EditProfileResponse>()
    }
  }
}
